// Historic Sales Page - View archived sale opportunities
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { saleAPI } from '../../services/api';
import api from '../../services/api';
import { useQuery } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchCrmEntities, fetchHorses } from '../../lib/queryFunctions';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Search, ChevronRight, Archive } from 'lucide-react';

const stageBadges = {
  presale_create: null,
  presale_confirm: { label: 'Tracking Deliverables', className: 'bg-yellow-100 text-yellow-700' },
  presale_deliverables: { label: 'Tracking Deliverables', className: 'bg-yellow-100 text-yellow-700' },
  presale_ready: { label: 'Ready for Sale', className: 'bg-green-100 text-green-700' },
  live_sale: { label: 'Live Sale', className: 'bg-primary text-white' },
  post_sale: { label: 'Complete', className: 'bg-muted text-muted-foreground' },
};

const resultBadges = {
  sold: { label: 'SOLD', className: 'bg-green-600 text-white' },
  rna: { label: 'RNA', className: 'bg-yellow-600 text-white' },
  withdrawn: { label: 'Withdrawn', className: 'bg-red-100 text-red-700' },
};

const HistoricSalesPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: allOpps = [], isLoading: loadingOpps } = useQuery({
    queryKey: QK.historicSaleOpportunities(),
    queryFn: () => api.get('/sale-opportunities?include_archived=true').then(r => r.data),
  });
  const opportunities = useMemo(() => allOpps.filter(o => o.archived === true), [allOpps]);

  const { data: salesData = [] } = useQuery({
    queryKey: QK.tenantSales(),
    queryFn: () => saleAPI.list().then(r => r.data),
  });
  const sales = salesData;

  const { data: allHorses = [] } = useQuery({
    queryKey: QK.horses({ include_archived: true }),
    queryFn: fetchHorses({ include_archived: true }),
  });
  const horses = useMemo(() => Object.fromEntries(allHorses.map(h => [h.id, h])), [allHorses]);

  const { data: allEntities = [] } = useQuery({
    queryKey: QK.crmEntities(),
    queryFn: fetchCrmEntities(),
  });
  const owners = useMemo(() => {
    const map = {};
    opportunities.forEach(opp => {
      if (opp.owner_crm_entity_ids?.length) {
        map[opp.id] = opp.owner_crm_entity_ids
          .map(id => allEntities.find(e => e.id === id)?.system_name)
          .filter(Boolean)
          .join(', ');
      }
    });
    return map;
  }, [opportunities, allEntities]);

  const loading = loadingOpps;

  const getOwnerNames = (opp) => owners[opp.id] || '-';

  const getSaleById = (id) => sales.find((s) => s.id === id);

  const getFullSaleName = (opp) => {
    if (opp.opportunity_name && opp.opportunity_name.includes(' - ')) {
      return opp.opportunity_name.split(' - ')[0];
    }
    const sale = getSaleById(opp.sale_id);
    return sale?.sale_name || '-';
  };

  const filteredOpportunities = opportunities.filter((opp) => {
    const searchLower = searchTerm.toLowerCase();
    const horseName = horses[opp.horse_id]?.system_name || '';
    const ownerNames = owners[opp.id] || '';
    const saleName = getFullSaleName(opp);
    const hipNumber = opp.hip_number || '';
    const result = opp.result || '';
    const salePrice = opp.sale_price ? opp.sale_price.toString() : '';
    const stageName = stageBadges[opp.stage]?.label || '';
    
    return (
      opp.opportunity_name?.toLowerCase().includes(searchLower) ||
      horseName.toLowerCase().includes(searchLower) ||
      ownerNames.toLowerCase().includes(searchLower) ||
      saleName.toLowerCase().includes(searchLower) ||
      hipNumber.toLowerCase().includes(searchLower) ||
      result.toLowerCase().includes(searchLower) ||
      salePrice.includes(searchLower) ||
      stageName.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <SidebarLayout>
        <div className="space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-semibold">View Historic Sale Opportunities</h1>
            <p className="text-muted-foreground mt-1">View archived sale opportunities</p>
          </div>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search historic opportunities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Historic Opportunities Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5" />
              Archived Opportunities ({filteredOpportunities.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sale</TableHead>
                  <TableHead>Horse</TableHead>
                  <TableHead>Owner(s)</TableHead>
                  <TableHead>Presale Stage</TableHead>
                  <TableHead>Hip #</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Sale Price</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOpportunities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <Archive className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                      <p className="text-muted-foreground">
                        {searchTerm ? 'No matching archived opportunities' : 'No archived opportunities yet'}
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOpportunities.map((opp) => {
                    const horse = horses[opp.horse_id];
                    const resultBadge = resultBadges[opp.result];

                    return (
                      <TableRow
                        key={opp.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/sales/opportunities/${opp.id}`)}
                      >
                        <TableCell className="text-sm">{getFullSaleName(opp)}</TableCell>
                        <TableCell className="text-sm font-medium">{horse?.system_name || '-'}</TableCell>
                        <TableCell className="text-sm">{getOwnerNames(opp)}</TableCell>
                        <TableCell className="text-sm">
                          {stageBadges[opp.stage] ? (
                            <Badge className={stageBadges[opp.stage].className}>{stageBadges[opp.stage].label}</Badge>
                          ) : (
                            <span>-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">#{opp.hip_number || 'TBD'}</TableCell>
                        <TableCell className="text-sm">
                          {resultBadge ? (
                            <Badge className={resultBadge.className}>{resultBadge.label}</Badge>
                          ) : (
                            <span>-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm font-medium">
                          {opp.sale_price ? `$${opp.sale_price.toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </SidebarLayout>
  );
};

export default HistoricSalesPage;
